/**
 * Jobs + Job Categories — DataCentral v4 `/jobs` and `/job-categories` endpoints.
 * Field names are lifted verbatim from the Go backend structs (job.types.go,
 * db/sqlc/job.sql.go, db/sqlc/job_category.sql.go). Shapes verified against source.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import qs from 'query-string';
import { dataCentralApi } from '@/api/client';
import type { ResponseArray, ResponseObject } from '@/types/shared';

/* ── Types ─────────────────────────────────────────────────────────────── */

export interface JobCategory {
  id: number;
  title: string;
  description: string;
  created_at: string;
  updated_at: string;
}

/** Verified live: categories is number[], public is bool, required_skills is string|null. */
export interface Job {
  job_id: number;
  title: string;
  location: string;
  status: string;
  slug: string;
  locale: string;
  type: string;
  summary: string;
  benefit: string;
  responsible: string;
  qualification: string;
  description: string;
  level: string;
  country: string;
  salary: string;
  /** Array of category IDs — verified live as number[]. */
  categories: number[];
  public: boolean;
  employee_updated: number;
  created_at: string;
  updated_at: string;
  department: string;
  required_skills: string | null;
}

export interface JobCreatePayload {
  title: string;
  location: string;
  status: string;
  locale: string;
  type: string;
  summary?: string;
  benefit: string;
  responsible: string;
  qualification: string;
  description: string;
  level: string;
  country: string;
  salary: string;
  /** JSON-encoded array of category IDs. */
  categories: number[];
  department: string;
  required_skills?: string;
}

export type JobUpdatePayload = Partial<JobCreatePayload>;

export interface JobCategoryCreatePayload {
  title: string;
  description: string;
}

export type JobCategoryUpdatePayload = Partial<JobCategoryCreatePayload>;

/* ── Params ────────────────────────────────────────────────────────────── */

export interface JobListParams {
  page?: number;
  page_size?: number;
  q?: string;
  status?: string;
  locale?: string;
}

export interface JobCategoryListParams {
  page?: number;
  page_size?: number;
  q?: string;
}

/* ── Query keys ────────────────────────────────────────────────────────── */

export const jobKeys = {
  all: ['jobs'] as const,
  list: (p: JobListParams) => ['jobs', 'list', p] as const,
  detail: (id: number) => ['jobs', 'detail', id] as const,
  categories: {
    all: ['job-categories'] as const,
    list: (p: JobCategoryListParams) => ['job-categories', 'list', p] as const,
    detail: (id: number) => ['job-categories', 'detail', id] as const,
    allList: () => ['job-categories', 'all'] as const,
  },
};

/* ── Helpers ────────────────────────────────────────────────────────────── */

function jobListQuery(params: JobListParams): string {
  const q: Record<string, string | number> = {};
  if (params.page) q.page = params.page;
  if (params.page_size) q.page_size = params.page_size;
  if (params.q) q.q = params.q;
  if (params.status) q.status = params.status;
  if (params.locale) q.locale = params.locale;
  return qs.stringify(q);
}

/* ── Job hooks ─────────────────────────────────────────────────────────── */

export function useJobs(params: JobListParams = {}) {
  return useQuery({
    queryKey: jobKeys.list(params),
    queryFn: async (): Promise<ResponseArray<Job>> => {
      const res = await dataCentralApi.get<ResponseArray<Job>>(
        `/jobs?${jobListQuery({ page: 1, page_size: 20, ...params })}`,
      );
      return res.data;
    },
  });
}

export function useJobDetail(id: number) {
  return useQuery({
    queryKey: jobKeys.detail(id),
    enabled: id > 0,
    queryFn: async (): Promise<ResponseObject<Job>> => {
      const res = await dataCentralApi.get<ResponseObject<Job>>(`/jobs/${id}`);
      return res.data;
    },
  });
}

export function useCreateJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: JobCreatePayload): Promise<ResponseObject<Job>> => {
      const res = await dataCentralApi.post<ResponseObject<Job>>('/jobs', payload);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: jobKeys.all });
    },
  });
}

export function useUpdateJob(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: JobUpdatePayload): Promise<ResponseObject<Job>> => {
      const res = await dataCentralApi.put<ResponseObject<Job>>(`/jobs/${id}`, payload);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: jobKeys.all });
    },
  });
}

export function usePublicJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<ResponseObject<Job>> => {
      const res = await dataCentralApi.put<ResponseObject<Job>>(`/jobs/${id}/public`);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: jobKeys.all });
    },
  });
}

export function useUnpublicJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<ResponseObject<Job>> => {
      const res = await dataCentralApi.put<ResponseObject<Job>>(`/jobs/${id}/un-public`);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: jobKeys.all });
    },
  });
}

export function useDeleteJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await dataCentralApi.delete(`/jobs/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: jobKeys.all });
    },
  });
}

/* ── Job Category hooks ─────────────────────────────────────────────────── */

export function useJobCategories(params: JobCategoryListParams = {}) {
  return useQuery({
    queryKey: jobKeys.categories.list(params),
    queryFn: async (): Promise<ResponseArray<JobCategory>> => {
      const q = qs.stringify({ page: 1, page_size: 20, ...params });
      const res = await dataCentralApi.get<ResponseArray<JobCategory>>(
        `/job-categories?${q}`,
      );
      return res.data;
    },
  });
}

/** Fetches all categories (no pagination cap) for use in dropdowns. */
export function useAllJobCategories() {
  return useQuery({
    queryKey: jobKeys.categories.allList(),
    queryFn: async (): Promise<ResponseArray<JobCategory>> => {
      const res = await dataCentralApi.get<ResponseArray<JobCategory>>(
        '/job-categories?page=1&page_size=200',
      );
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useJobCategoryDetail(id: number) {
  return useQuery({
    queryKey: jobKeys.categories.detail(id),
    enabled: id > 0,
    queryFn: async (): Promise<ResponseObject<JobCategory>> => {
      const res = await dataCentralApi.get<ResponseObject<JobCategory>>(
        `/job-categories/${id}`,
      );
      return res.data;
    },
  });
}

export function useCreateJobCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: JobCategoryCreatePayload): Promise<ResponseObject<JobCategory>> => {
      const res = await dataCentralApi.post<ResponseObject<JobCategory>>(
        '/job-categories',
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: jobKeys.categories.all });
    },
  });
}

export function useUpdateJobCategory(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: JobCategoryUpdatePayload): Promise<ResponseObject<JobCategory>> => {
      const res = await dataCentralApi.put<ResponseObject<JobCategory>>(
        `/job-categories/${id}`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: jobKeys.categories.all });
    },
  });
}

export function useDeleteJobCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await dataCentralApi.delete(`/job-categories/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: jobKeys.categories.all });
    },
  });
}
